import dayjs from "dayjs"
import { get, set } from "lodash-es"
import { action, makeObservable, observable } from "mobx"

import {
  emailConfig,
  eventStatusType,
  fixContentsType,
  moduleType,
  positionItem,
  variablesType,
} from "@/pages/EmailEdit/type"

// 通过id值聚焦input
const focusInputById = (id: string, isDelay?: boolean, delay = 20) => {
  function focusInput() {
    const targetElement = document.getElementById(id) as HTMLInputElement

    if (targetElement) {
      targetElement?.focus()
      targetElement.selectionStart = targetElement.value.length // 设置光标位置为内容的末尾
      targetElement.selectionEnd = targetElement.value.length // 设置选中范围的结束位置为内容的末尾
    }
  }
  if (isDelay) {
    setTimeout(() => {
      focusInput()
    }, delay)
  } else {
    focusInput()
  }
}

class EmailEditStore {
  // 页面当前Tab选中状态
  @observable public tabIndex = 0

  // 页面Loading状态
  @observable public loading = false

  // 保存按钮Loading状态
  @observable public saveLoading = false

  // 保存按钮活跃状态
  @observable public saveBtnActive = false

  // 页面当前激活编辑区域
  @observable public activeModule?: moduleType

  // 当前Event 状态
  @observable public eventStatus: eventStatusType =
    eventStatusType.ReturnRequestConfirmation

  // email整体配置
  @observable public config: emailConfig = {
    body: [],
    button: [],
    style: { button_color: "", logo: "", logo_width: 50, text: "", type: 0 },
    subject: "",
    title: "",
    type: "",
    code: "",
  }

  // email变量
  @observable public variables: variablesType[] = []

  // 固定文本值
  @observable public fixContents: fixContentsType[] = []

  // 值加载时间
  @observable dataLoadedTime: number = 0

  constructor() {
    makeObservable(this)
  }

  /**
   * 设置配置
   */
  @action.bound
  public setConfig(config: emailConfig): void {
    this.dataLoadedTime = dayjs().unix()

    this.config = config
  }

  /**
   * 设置Tab的选中index
   */
  @action.bound
  public setTabIndex(index: number): void {
    this.tabIndex = index
  }

  /**
   * 切换状态
   */
  @action.bound
  public setEventStatus(status?: eventStatusType): void {
    if (status) {
      this.eventStatus = status
    }
  }

  /**
   * 设置Body配置
   */
  @action.bound
  setBodyConfig(value, index) {
    this.config.body[index].content = value
  }

  @action.bound
  getBodyByPosition(positionId): positionItem | undefined {
    return this.config.body.find((item) => item.position === positionId)
  }

  @action.bound
  getButtonByPosition(positionId): positionItem | undefined {
    return this.config.button.find((item) => item.position === positionId)
  }

  /**
   * 设置Button配置
   */
  @action.bound
  setButtonConfig(value, index) {
    this.config.button[index].content = value
  }

  /**
   * 局部设置Config配置
   */
  @action.bound
  setTargetConfig<T>(path: string, v: T) {
    set(this.config, path, v)
  }
  /**
   * 局部设置Config配置
   */
  @action.bound
  setStyleConfig<T>(path: string, v: T) {
    set(this.config.style, path, v)
  }

  /**
   * 解析字符串中的字段变量
   */
  @action.bound
  public parseFiledValue(val: string | undefined): string | undefined {
    if (!val) return val

    let value = val

    this.variables.forEach((variable) => {
      if (value.indexOf(variable.key) !== -1 && variable.value) {
        value = value.replaceAll(variable.key, variable.value)
      }
    })
    return value
  }
  /**
   * 切换当前编辑区域
   */
  @action.bound
  public addVarSpan(val: string | undefined): string | undefined {
    if (!val) return val

    let value = val

    this.variables.forEach((variable) => {
      if (value.indexOf(variable.key) !== -1) {
        value = value.replaceAll(
          variable.key,
          `<span>${variable.value || variable.key}</span>`,
        )
      }
    })
    return value
  }
  /**
   * 切换当前编辑区域
   */
  @action.bound
  public getFiledValue(path: string): string | undefined {
    const value = get(this.config, path)

    return this.parseFiledValue(value)
  }

  /**
   * 切换当前编辑区域
   */
  @action.bound
  public setActiveModule(type?: moduleType, toFocus = true): void {
    // 如果为当前，则返回不执行后面逻辑
    if (type === this.activeModule) return

    this.activeModule = type
    
    if (toFocus) {
      // 左右侧内容联动
      switch (type) {
        case moduleType.title:
          this.tabIndex = 0
          focusInputById("return-title-input", true)
          break
        case moduleType.position1:
          this.tabIndex = 0
          focusInputById("return-body_position1", true)
          break
        case moduleType.position2:
          this.tabIndex = 0
          focusInputById("return-body_position2", true)
          break
        case moduleType.position3:
          this.tabIndex = 0
          focusInputById("return-body_position3", true)
          break
        case moduleType.position4:
          this.tabIndex = 0
          focusInputById("return-body_position4", true)
          break
        case moduleType.position5:
          this.tabIndex = 0
          focusInputById("return-body_position5", true)
          break
        case moduleType.buttonName1:
          this.tabIndex = 0
          focusInputById("return-button_position1", true)
          break
        case moduleType.buttonName2:
          this.tabIndex = 0
          focusInputById("return-button_position2", true)
          break
        case moduleType.logo:
          this.tabIndex = 1
          break
        case moduleType.storeName:
          this.tabIndex = 1
          focusInputById("return-store-name-input", true)
          break
      }
    }
  }

  /**
   * 切换当前保存激活状态
   */
  @action.bound
  public setSaveBtnActive(type: boolean): void {
    this.saveBtnActive = type
  }
  /**
   * 切换当前Loading状态
   */
  @action.bound
  public setLoading(type: boolean): void {
    this.loading = type
  }
  /**
   * 切换保存Loading状态
   */
  @action.bound
  public setSaveLoading(type: boolean): void {
    this.saveLoading = type
  }
  /**
   * 设置collection选项
   */
  @action.bound
  public setVariables(data: variablesType[]): void {
    this.variables = data
  }

  /**
   * 设置固定值FixContent
   */
  @action.bound
  public setFixContents(info: fixContentsType[]): void {
    this.fixContents = info
  }

  @action.bound
  setDataLoadedTime(time: number) {
    this.dataLoadedTime = time
  }
}

export default EmailEditStore
